<template>
  <div>
    <div v-if="firstTime">
      <div class="flex flex-col gap-4 text-left">
        <p class="pt-2 text-left text-sm text-dark-gray">
          Enter the email address(es) OR domain(s) {{ companyLegalName }} uses
          to send rate confirmations. This will be shared with carriers who need
          to authenticate a rate confirmation received from your company.
        </p>
        <div>
          <label class="w-full text-left text-2xs font-bold uppercase">
            rate con email (Required)
          </label>
          <RateConInput
            :email-or-domain="true"
            :data-stored="emailsAndDomains"
            @update:fields="handleEmailsOrDomain"
            @delete="handleRemove"
          />
        </div>
        <p class="pt-8 text-left text-sm text-dark-gray">
          Confirm the phone number(s) a carrier can call with concerns about a
          specific rate confirmation.
        </p>
        <div>
          <label class="w-full text-left text-2xs font-bold uppercase">
            rate con Phone # (Required)
          </label>
          <RateConInput
            :data-stored="phones"
            @update:fields="handlePhones"
            @delete="handleRemove"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col gap-4 text-left"
    >
      <p class="pt-2 text-sm text-dark-gray">
        Confirm the email address(es) {{ companyLegalName }} uses to send rate
        confirmations and the phone number(s) that can be called with any
        concerns.
      </p>

      <section>
        <label class="mb-1 text-xxs font-bold uppercase text-gray-over-dark">
          Email
        </label>
        <ul v-if="stored.emails.length">
          <li
            v-for="email in stored.emails"
            :key="email.id"
            class="my-1 text-sm text-dark-gray"
          >
            {{ email.value }}
          </li>
        </ul>
        <p v-else>-</p>
      </section>

      <section>
        <label class="mb-1 text-xxs font-bold uppercase text-gray-over-dark">
          Domain
        </label>
        <ul v-if="stored.domains.length">
          <li
            v-for="domain in stored.domains"
            :key="domain.id"
            class="my-1 text-sm text-dark-gray"
          >
            {{ domain.value }}
          </li>
        </ul>
        <p v-else>-</p>
      </section>

      <section>
        <label class="mb-1 text-xxs font-bold uppercase text-gray-over-dark">
          Phone
        </label>
        <ul v-if="stored.phones.length">
          <li
            v-for="phone in stored.phones"
            :key="phone.id"
            class="my-1 text-sm text-dark-gray"
          >
            {{ phone.value }}
          </li>
        </ul>
        <p v-else>-</p>
      </section>
    </div>

    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      @next="handleNext"
      @back="handleBack"
      @edit="handleEdit"
    />
  </div>
</template>
<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { isDomain, isEmail, isPhone } from '@/common/validators.js';
import RateConInput from '@/components/RateConInput.vue';
import { useFormRouter } from '@/composables/formRouter';
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import { useIdentityThreeOperations } from '@/composables/useIdentityThreeOperations';

const store = useStore();
const componentIdentifier = 'Identity-3';
const formRouter = useFormRouter(componentIdentifier);
const useOperations = useIdentityThreeOperations();
const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
const activeStep = computed(
  () => store.state.formRoutes?.activeStep?.identifier
);
const firstTime = computed(
  () =>
    store.state.firstTime?.[formRouter.componentStep.value?.firstTimeIdentifier]
);
const fields = ref({
  emails: [],
  phones: [],
  domains: [],
});
const billingContact = computed(
  () => store.state.carriers.carrierData.identity.billing_contacts
);
const companyDomains = computed(
  () => store.state.carriers.carrierData.identity.company_domains
);
const onboardingPacketId = computed(
  () => store.state.onboardingPacket.onboarding_id
);
const carrierId = computed(
  () =>
    store.state.users.currentUser?.company?.carrier_id ||
    store.state.users.currentUser?.requested_carrier_id
);
const companyId = computed(() => store.state.users?.currentUser?.company?.id);
const companyLegalName = computed(
  () => store.state.users?.currentUser?.company?.legal_name
);
const stored = computed(() => ({
  emails:
    billingContact.value?.rate_con_emails.map((e) => ({
      ...e,
      type: 'email',
      is_type: undefined,
    })) || [],
  phones:
    billingContact.value?.rate_con_phones.map((e) => ({
      ...e,
      type: 'phone',
      is_type: undefined,
      value: e.value
        .replaceAll('(', '')
        .replaceAll(')', '')
        .replaceAll(' ', '-'),
    })) || [],
  domains: companyDomains.value.map((e) => ({ ...e, type: 'domain' })) || [],
}));

const isStoredEmpty = computed(() => {
  return (
    stored.value.emails.length === 0 &&
    stored.value.phones.length === 0 &&
    stored.value.domains.length === 0
  );
});

const isFieldsEmpty = computed(() => {
  return (
    fields.value.emails.length === 0 &&
    fields.value.phones.length === 0 &&
    fields.value.domains.length === 0
  );
});

const emailsAndDomains = computed(() => {
  const data = [...stored.value.emails, ...stored.value.domains];
  if (!data.length) return [{ value: '', type: 'email' }];
  return data;
});
const phones = computed(() => {
  if (!stored.value.phones.length) return [{ value: '', type: 'phone' }];
  return stored.value.phones;
});
const buttonsToDisplay = computed(() => {
  if (firstTime.value) return 'continue-save';
  return 'continue-edit';
});

const handleNext = async () => {
  store.commit(
    'addFormDebugMessage',
    componentIdentifier + ": 'handleNext' called"
  );
  if (isNavigationDisabled.value)
    return store.commit(
      'addFormDebugMessage',
      'ERROR: ' + componentIdentifier + ': formRouter is disabled, returning'
    );

  const modifiedFields = useOperations.getModifiedFields(
    stored.value,
    fields.value
  );
  await useOperations.saveData(
    modifiedFields,
    carrierId.value,
    companyId.value,
    onboardingPacketId.value
  );
  formRouter.moveToNextStep();

  if (!isFieldsEmpty.value) getOnboardingPacket();

  fields.value = {
    emails: [],
    phones: [],
    domains: [],
  };
};

const handleBack = () => {
  store.commit(
    'addFormDebugMessage',
    componentIdentifier + ": 'handleBack' called"
  );
  if (isNavigationDisabled.value)
    return store.commit(
      'addFormDebugMessage',
      'ERROR: ' + componentIdentifier + ': formRouter is disabled, returning'
    );
  formRouter.moveToPreviousStep();
};

const handleEdit = () => {
  store.commit(
    'addFormDebugMessage',
    componentIdentifier + ": 'handleEdit' called"
  );
  if (isNavigationDisabled.value)
    return console.log(
      'ERROR: ' + componentIdentifier + ': formRouter is disabled, returning'
    );
  formRouter.editActiveStep();
};

const handleRemove = (record) => {
  if (!record) return;

  const { id, type } = record || {};
  useOperations.handleDelete(capitalizeFirstLetter(type), id);
  getOnboardingPacket();
};

const capitalizeFirstLetter = (string) => {
  if (typeof string !== 'string' || string.length === 0) {
    return '';
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
};
const handleEmailsOrDomain = (updatedFields) => {
  fields.value.emails = updatedFields.email;
  fields.value.domains = updatedFields.domain;
};

const handlePhones = (updatedPhones) => {
  fields.value.phones = updatedPhones.phone;
};

const getOnboardingPacket = async () => {
  await store.dispatch('onboardingPacket/getOnboardingPacketById', {
    id: store.state.onboardingPacket.onboarding_id,
  });
};

const validateCurrentStep = () => {
  const phones =
    fields.value.phones.length > 0
      ? fields.value.phones
      : stored.value.phones || [];
  const emails =
    fields.value.emails.length > 0
      ? fields.value.emails
      : stored.value.emails || [];

  const validEmails = emails.every(
    (email) => email.value && isEmail(email.value)
  );
  const validDomains = fields.value.domains.every(
    (domain) => domain.value && isDomain(domain.value)
  );

  const validPhones = phones.every(
    (phone) => phone.value && isPhone(phone.value)
  );

  const hasEmailOrDomain =
    (emails.length > 0 && validEmails) ||
    (fields.value.domains.length > 0 && validDomains);
  const hasPhone = phones.length > 0 && validPhones;

  const isValid = hasEmailOrDomain && hasPhone;

  store.commit('toggleDisabledButton', {
    bool: !isValid,
    source: 'IdentityStepThree #2',
  });
};

watch(activeStep, (newValue) => {
  if (newValue === componentIdentifier) {
    if (isStoredEmpty.value) {
      store.commit('toggleDisabledButton', {
        bool: true,
        source: 'IdentityStepThree #1',
      });

      store.commit('firstTime/updateStepFirstTime', {
        step: 'Identity',
        value: true,
      });
    } else {
      store.commit('toggleDisabledButton', {
        bool: false,
        source: 'IdentityStepThree #3',
      });
      store.commit('firstTime/updateStepFirstTime', {
        step: 'Identity',
        value: false,
      });
    }
  }
});

watch(
  fields,
  () => {
    if (activeStep.value !== componentIdentifier) return;
    validateCurrentStep();
  },
  { deep: true }
);
</script>
